import _ from 'lodash'

import { useContentful } from '@/components/common/contentful/composables/useContentful'
import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'

import useI18n from '@/composables/useI18n'
import { useTransformEntries } from '@/composables/useTransformEntries'
import { PRICE_CODES_TO_TABLE_FIELDS, WIDGET_TYPES } from '@/settings/constants'

export function useTransformProduct() {
  const { getImageUrl, getImageAlt, getContentTypeId } = useContentfulComponent()
  const { transformEntries } = useTransformEntries()
  const contentful = useContentful()
  const { t } = useI18n()

  function transformProduct(entry) {
    const product = entry.fields
    if (!product) return null
    const imgList = product.images?.length
      ? product.images.map((item) => {
          return {
            title: item.fields.title,
            alt: getImageAlt(item, item.fields.title),
            url: getImageUrl(item, 70, 540),
          }
        })
      : product.types?.some((type) => type.fields?.slug?.toLowerCase() === 'services')
        ? []
        : [
            {
              title: 'Default Image',
              alt: 'Default Product Image',
              url: contentful.service.getProductDefaultImage(),
            },
          ]

    const qualitySpecsList = product.qualitySpecifications
      ? product.qualitySpecifications.map((el) => el.fields).filter((el) => el)
      : null
    const qualityDocsList = product.qualityDocumentation
      ? transformEntries(product.qualityDocumentation)
      : null

    const faqList = product.faq ? product.faq.map((el) => el.fields).filter((el) => el) : null

    const protocolsAndIfusList = product.protocolsAndIfus
      ? transformEntries(product.protocolsAndIfus)
      : null
    const additionalResourcesList = product.additionalResources
      ? transformEntries(product.additionalResources)
      : null

    const tabsList = {
      details: {
        id: 'details',
        title: t('product.tabs.details'),
        text: product.descriptionRich || '',
        type: 'text',
      },
      quality: {
        id: 'quality',
        title: t('product.tabs.quality'),
        type: 'nestedList',
        sectionsList: [
          {
            title: t('product.tabs.qualitySpecs'),
            list: qualitySpecsList?.length ? qualitySpecsList : null,
          },
          {
            title: t('product.tabs.qualityDocs'),
            list: qualityDocsList?.length ? qualityDocsList : null,
          },
        ].filter((el) => el.list?.length),
      },
      faq: {
        id: 'faq',
        title: t('product.tabs.faq'),
        list: faqList?.length ? faqList : null,
        type: 'list',
      },
      resources: {
        id: 'resources',
        title: t('product.tabs.resources'),
        type: 'nestedList',
        sectionsList: [
          {
            title: t('product.tabs.protocolsAndIfus'),
            list: protocolsAndIfusList?.length
              ? protocolsAndIfusList.filter((el) => el.payloadUrl || el.slug)
              : null,
          },
          {
            title: t('product.tabs.additionalResources'),
            list: additionalResourcesList?.length
              ? additionalResourcesList.filter((el) => el.payloadUrl || el.slug)
              : null,
          },
        ].filter((el) => el.list?.length),
      },
    }
    for (const tabId in tabsList) {
      const isTabValid = (tab) => {
        if (tab.type === 'text') return tab.text
        if (tab.type === 'nestedList') return tab.sectionsList?.length
        if (tab.type === 'list') return tab.list?.length
      }
      if (!isTabValid(tabsList[tabId])) delete tabsList[tabId]
    }

    const relatedProductsList = transformEntries(product.relatedProducts)

    const { customizations, isCellProduct, isVialsPerDonorProduct, groupList } =
      extactCustomizations(product)

    // Inventory Data
    const productTypes = product.types?.filter((el) => el.fields).map((el) => el.fields.title) || []
    const productSpecies = product.species
      ? product.species.filter((el) => el.fields).map((el) => el.fields.title)
      : []
    const inventoryData = [
      {
        title: 'Product Type',
        selected: productTypes,
      },
      {
        title: 'Species',
        selected: productSpecies,
      },
    ].filter((el) => el.selected.length)
    return {
      slug: product.slug,
      title: product.title,
      subtitle: product.description,
      images: imgList,
      heroImage: product.heroImage,
      tabs: tabsList,
      relatedProducts: relatedProductsList,
      serviceRelatedProducts:
        relatedProductsList?.map((entry) => {
          return {
            link: entry.link,
            title: entry.title,
            categoryTitle: entry.parent?.title || '',
            categoryLink: entry.parent?.link || '',
            image: entry.img,
          }
        }) || [],
      customizationsTitle: product.customizationsTitle,
      customizationsSubtitle: product.customizationsSubtitle,
      customizations,
      sageCode: product.sageCode || null,
      isInventory: !!product.isInventory,
      inventoryData,
      noCustomSku: product.noCustomSku,
      groupList,
      isNHAProduct: product.customizations?.some((el) =>
        el.fields?.name?.toLowerCase()?.includes('nha'),
      ),
      isCellProduct,
      isVialsPerDonorProduct,
      isServicesProduct: product.types?.some(
        (type) => type.fields?.slug?.toLowerCase() === 'services',
      ),
      configurationsRelationship: product.configurationsRelationship?.fields?.items
        ?.filter(
          (el) =>
            el?.fields && el?.sys?.contentType?.sys?.id === 'productOptionsRelationshipWrapper',
        )
        .map((el) => el.fields),
      isMaxQuantityRestricted: typeof product.maximumQuantity === 'number',
      maxQuantity: product.maximumQuantity,
      maxQuantityAffectingField: {
        title: product.fieldAffectingMaximumQuantity?.fields?.displayName || '',
        values: product.fieldValueAffectingMaximumQuantity?.length
          ? product.fieldValueAffectingMaximumQuantity.map((el) => el.trim())
          : [],
        isAllValuesAffectMaxQuantity:
          !product.fieldValueAffectingMaximumQuantity?.length ||
          product.fieldAffectingMaximumQuantity?.sys?.contentType?.sys?.id === 'productATCFreeText',
      },
      quantityLabel: (product.quantityBoxLabel || t('common.qtyTitle') || '') + ':',
    }
  }

  function extactCustomizations(product) {
    let isCellProduct = false
    let isVialsPerDonorProduct = false

    const typesCustomizationList = product.types?.length
      ? []
          .concat(
            ...product.types.map((typeEntry) => {
              return typeEntry.fields?.includes?.length
                ? typeEntry.fields.includes.filter((el) => getContentTypeId(el) === 'productATC')
                : null
            }),
          )
          .filter((el) => el)
      : []

    const subtypesCustomizationList = product.subtypes?.length
      ? []
          .concat(
            ...product.subtypes.map((subtypeEntry) => {
              return subtypeEntry.fields?.includes?.length
                ? subtypeEntry.fields.includes.filter((el) => getContentTypeId(el) === 'productATC')
                : null
            }),
          )
          .filter((el) => el)
      : []

    const customizationsSortedList = product.customizations?.filter((el) => el.fields) || []
    const generalCustomizationList = [
      ...customizationsSortedList,
      ...subtypesCustomizationList,
      ...typesCustomizationList,
    ].sort((a, b) => a.fields.rank - b.fields.rank)

    const groupSetList = new Set()
    const customizationsSetList = new Set()

    if (generalCustomizationList.length) {
      const nestedFieldNames = new Set(
        generalCustomizationList.reduce((nestedFieldNamesSum, item) => {
          const customizationEntries = item.fields?.fields
          if (!customizationEntries?.length) return nestedFieldNamesSum
          const itemNestedNames = customizationEntries.reduce(
            (nestedCustomizationNamesSum, customizationEntry) => {
              const isAtcGroup = getContentTypeId(customizationEntry) === 'productATCGroup'
              const nestedNames = isAtcGroup
                ? customizationEntry.fields?.fields?.reduce(
                    (groupNestedNamesSum, groupCustomizationEntry) => {
                      const groupNestedNames = groupCustomizationEntry.fields?.nestedFields || []
                      return [...groupNestedNamesSum, ...groupNestedNames]
                    },
                    [],
                  ) || []
                : customizationEntry.fields?.nestedFields || []
              return [...nestedCustomizationNamesSum, ...nestedNames]
            },
            [],
          )
          return [...nestedFieldNamesSum, ...itemNestedNames]
        }, []),
      )

      //construct flattened Sets of configuration fields and field groups
      generalCustomizationList.forEach((item) => {
        const customizationEntries = item.fields?.fields || []
        if (!customizationEntries.length) return
        customizationEntries.forEach((customizationEntry) => {
          const isAtcGroup = getContentTypeId(customizationEntry) === 'productATCGroup'
          if (isAtcGroup) {
            const group = {
              name: customizationEntry.fields?.name,
              description: customizationEntry.fields?.description,
              collapsed: customizationEntry.fields?.collapsed,
              isDefault: false,
            }
            groupSetList.add(group)
            const groupEntries = customizationEntry.fields?.fields
            if (groupEntries?.length) {
              groupEntries.forEach((groupEntry) => {
                groupEntry.group = group
                if (nestedFieldNames.has(groupEntry.fields?.displayName)) {
                  groupEntry.nested = true
                }
                customizationsSetList.add(groupEntry)
              })
            }
          } else {
            if (nestedFieldNames.has(customizationEntry.fields?.displayName)) {
              customizationEntry.nested = true
            }
            customizationsSetList.add(customizationEntry)
          }
        })
      })
    }

    const customizationsArrayList = Array.from(customizationsSetList)
    const groupList = Array.from(groupSetList).concat([
      {
        isDefault: true,
      },
    ])
    const hiddenFreeTextCustomizations = []

    let customizations = customizationsArrayList
      .map((item) => {
        if (item.fields) {
          let iconsList, defaultImgTitle
          if (item.fields.imageValue) {
            iconsList = item.fields.imageValue.map((item) => {
              return {
                title: item.fields.title,
                img: getImageUrl(item),
                imgAlt: getImageAlt(item, item.fields.title),
              }
            })
            defaultImgTitle = item.fields.defaultValue
          }

          const isQtyField = getContentTypeId(item) === 'productATCNumberSelector'

          const numberSelectorDefaultValue = isQtyField
            ? typeof item.fields.defaultValue === 'number'
              ? item.fields.defaultValue || 1
              : item.fields.value
                ? item.fields.value[0]
                : 1
            : null

          const defaultValue =
            item.fields.value?.length === 1 ? item.fields.value[0] : item.fields.defaultValue

          let tableField = ''
          const itemName = item.fields.name
          const searchLength = itemName.length - 4

          for (let i = 0; i < searchLength; i++) {
            if (itemName[i] === '$') {
              const code = itemName.slice(i + 1, i + 5).toUpperCase()
              const field = PRICE_CODES_TO_TABLE_FIELDS[code]
              if (field) {
                if (field === 'vialsPerLot' || field === 'cellQty') {
                  isCellProduct = true
                }
                if (field === 'vialsPerDonor') {
                  isVialsPerDonorProduct = true
                }

                tableField = field
                break
              }
            }
          }

          const options =
            Array.isArray(item?.fields?.value) && item.fields.value.length
              ? item.fields.value.map((el) => el.trim())
              : null

          const textarea =
            getContentTypeId(item) === 'productATCFreeText'
              ? {
                  placeholder:
                    item.fields.helperText || item.fields.displayName || item.fields.name,
                  uiDisplay: item.fields.uiDisplay || item.fields.uiDisplay === undefined,
                  initialUiDisplay: item.fields.uiDisplay || item.fields.uiDisplay === undefined,
                }
              : null

          const cards = getContentTypeId(item) === 'productATCCards'
          const cardsOptions =
            item.fields.options?.map((option) => {
              return { ...option.fields }
            }) || null

          const getDroplistType = (item, options) => {
            const appearanceMap = {
              Chips: 'chips',
              'Drop List': 'dropList',
              Auto: 'auto',
            }
            const droplistType = appearanceMap[item.fields.valueAppearance] || appearanceMap.Auto
            return droplistType === 'auto'
              ? options.length > 10
                ? WIDGET_TYPES.dropList
                : WIDGET_TYPES.chips
              : droplistType
          }
          const widgetType = isQtyField
            ? WIDGET_TYPES.qty
            : textarea
              ? WIDGET_TYPES.textarea
              : cards
                ? WIDGET_TYPES.cards
                : options?.length
                  ? getDroplistType(item, options)
                  : item.fields.imageValue?.length
                    ? WIDGET_TYPES.iconGroup
                    : null

          const selectedValue =
            widgetType === WIDGET_TYPES.iconGroup
              ? defaultImgTitle
              : isQtyField
                ? numberSelectorDefaultValue
                : defaultValue || null

          const children = item.fields.nestedFields?.length
            ? item.fields.nestedFields
                .map((nestedFieldName) =>
                  customizationsArrayList.find((el) => el.fields?.displayName === nestedFieldName),
                )
                .filter((el) => el)
            : []
          const parentEntry = customizationsArrayList.find((customization) => {
            return customization.fields?.nestedFields?.length
              ? customization.fields.nestedFields.some(
                  (nestedFieldName) => nestedFieldName === item.fields.displayName,
                )
              : false
          })
          const iconGroup = item.fields.imageValue?.length ? [...iconsList] : null
          return {
            widgetType,
            tableField,
            isNumericFreeText:
              ['volPerLot', 'aliquotQty', 'customCellsPerLot', 'customCellQty'].includes(
                tableField,
              ) && !!textarea,
            groupName: item.group?.name,
            title: item.fields.displayName?.trim() || item.fields.name.trim(),
            subtitle: item.fields.description || null,
            options,
            allOptions: options,
            cardsOptions,
            selected: selectedValue || null,
            defaultValue: selectedValue || null,
            minValue: typeof item.fields.minValue === 'number' ? item.fields.minValue : 1,
            maxValue: item.fields.maxValue || null,
            increment: item.fields.incrementAmount || 1,
            required: item.fields.mandatory,
            iconGroup,
            allIconGroup: iconGroup,
            isOptionsWidget: options?.length || iconGroup?.length,
            info: item.fields.infoBubleValue || item.fields.toolTip || null,
            textarea,
            group: item.fields.grouping,
            serverField: item.fields.serverField || null,
            forceCustom:
              (item.fields.customProduct === undefined &&
                getContentTypeId(item) === 'productATCFreeText') ||
              !!item.fields.customProduct,
            isHidden: false,
            nested: !!item.nested,
            children,
            parent: parentEntry?.fields
              ? {
                  title: parentEntry.fields.displayName,
                  serverField: parentEntry.fields.serverField,
                }
              : null,
          }
        }
      })
      .filter((el) => {
        if (el) {
          if (el.textarea && !el.textarea.uiDisplay) {
            hiddenFreeTextCustomizations.push(el)
          } else {
            return el
          }
        }
      })
    return {
      isCellProduct,
      isVialsPerDonorProduct,
      groupList,
      customizations: [...customizations, ...hiddenFreeTextCustomizations].sort((a, b) => {
        const aHas = typeof a.group !== 'undefined'
        const bHas = typeof b.group !== 'undefined'
        return (
          bHas - aHas ||
          (aHas === true && a.group?.localeCompare(b.group, 'en', { sensitivity: 'base' })) ||
          0
        )
      }),
    }
  }

  function updateTableValue(field, qty, qtyTable, isCellProduct) {
    switch (field) {
      case 'volPerLot':
        qtyTable.qtyPerLot = qty || 0
        break
      case 'vialsPerLot':
        qtyTable.aliquots = qty || 0
        break
      case 'aliquotQty':
      case 'cellQty':
      case 'customCellQty':
        qtyTable.aliquotQty = qty || 0
        break
      case 'vialsPerDonor':
        qtyTable.vialsPerDonor = qty || 0
        break
    }
    if (!isCellProduct) {
      const aliquots = qtyTable.qtyPerLot
        ? qtyTable.aliquotQty
          ? qtyTable.qtyPerLot / qtyTable.aliquotQty
          : 1
        : 0
      qtyTable.aliquots = Math.floor(aliquots)
      qtyTable.remainder = !Number.isInteger(aliquots)
        ? Math.round((qtyTable.qtyPerLot % qtyTable.aliquotQty) * 10) / 10
        : 0
    } else {
      qtyTable.qtyPerLot = qtyTable.aliquots * qtyTable.aliquotQty
    }
  }
  function affectTableBySingleCustomization(customization, product, qtyTable, resetTableValue) {
    const field = customization.tableField
    const qty = customization.selected
    const isFreeText = customization.textarea

    const freeTextList = ['volPerLot', 'aliquotQty', 'customCellsPerLot', 'customCellQty']
    const dropdownList = ['cellQty']
    if (field === 'toggleAliquot') {
      if (qty?.toString()?.toLowerCase() === 'no') resetTableValue(field, product)
    } else if (freeTextList.includes(field)) {
      const cleanedQty = isFreeText ? qty | 0 : stripNonNumerics(qty)
      const isValid = isNumericValid(cleanedQty, product.isCellProduct)
      if (isValid) updateTableValue(field, +cleanedQty, qtyTable, product.isCellProduct)
    } else if (dropdownList.includes(field)) {
      const coeff = +qty?.toString()?.toLowerCase().split('x')[0]
      const valueToSet = isNaN(coeff) ? 0 : coeff
      updateTableValue(field, valueToSet, qtyTable, product.isCellProduct)
    } else {
      updateTableValue(field, qty, qtyTable, product.isCellProduct)
    }
  }

  function updateQtyTableBySelectedCustomizations(
    customizations,
    product,
    qtyTable,
    resetTableValue,
  ) {
    customizations.forEach((customization) => {
      if (customization.tableField)
        affectTableBySingleCustomization(customization, product, qtyTable, resetTableValue)
    })
  }

  function isSomeChildCustomizationVisible(customization, customizations) {
    if (!customization || !customization.children?.length) return false
    const isSomeChildVisible = customization.children.some((child) =>
      customizations.some(
        (el) =>
          el.title === child.fields.displayName &&
          el.serverField &&
          el.serverField === customization.serverField,
      ),
    )
    return isSomeChildVisible
  }

  function getCustomizationsAddToCartData(customizations) {
    return customizations
      .map((customization) => {
        const freeTextList = ['volPerLot', 'aliquotQty', 'customCellsPerLot', 'customCellQty']
        let selected = customization.selected
        if (customization.widgetType === 'cards') selected = customization.selected?.title
        if (freeTextList.includes(customization.tableField) && customization.selected) {
          const stripSelected = stripNonNumerics(selected)
          selected = stripSelected.length ? +stripSelected : null
        }
        if (customization.isHidden) return
        return {
          title: customization.title,
          selected,
          skuAttribute: !!customization.serverField,
        }
      })
      .filter((el) => el?.selected || typeof el?.selected === 'number')
  }

  function getAddToCartProductData(
    {
      customizations = [],
      sageCode = '',
      productUrl = window.location.pathname,
      quantity = 1,
      productImageUrl = '',
      isCellProduct = false,
      isVialsPerDonorProduct = false,
    } = {},
    qtyTable = {},
    activeSku = {},
    isCustomSku = false,
    totalPrice = '',
  ) {
    return {
      customizations: getCustomizationsAddToCartData(customizations),
      productImageUrl,
      productUrl,
      quantity,
      code: activeSku.code || sageCode,
      pricingParams: getPriceParams({
        salesUnit: activeSku.salesUnit,
        qtyTable,
        customizations,
        isCellProduct,
        isVialsPerDonorProduct,
      }),
      zeroPriceEntry: !totalPrice,
      isCustomSku,
      createNew: true,
    }
  }

  function getPriceParams({
    salesUnit = '',
    qtyTable = {},
    customizations = [],
    isCellProduct = false,
    isVialsPerDonorProduct = false,
  }) {
    return {
      salesToStock: getSalesToStock({
        unit: salesUnit?.toString()?.toLowerCase(),
        qtyTable,
        isCellProduct,
        isVialsPerDonorProduct,
      }),
      aliqPerLot: qtyTable.aliquots,
      volPerAliq: isCustomAliquot(getVisibleCustomizations(customizations))
        ? qtyTable.aliquotQty
        : qtyTable.qtyPerLot,
    }
  }

  function getVisibleCustomizations(customizations = []) {
    return (
      customizations.filter(
        (el) => !el.isHidden && (el.widgetType !== 'textarea' || el.textarea.uiDisplay),
      ) || []
    )
  }

  function getActiveServerCustomizations(visibleCustomizations) {
    const activeAndVisible = visibleCustomizations.filter(
      (el) => el.serverField && (el.selected || typeof el.selected === 'number'),
    )
    return activeAndVisible.filter(
      (customization) => !isSomeChildCustomizationVisible(customization, visibleCustomizations),
    )
  }

  function getSkuFiltersString(
    slug = '',
    includeServerCustomizations = true,
    activeServerCustomizations = [],
  ) {
    const allOptionsValue = 'no preference'
    const filters = ['sageDisplayFlag:true', 'myCatalog:false']
    const slugQuery = `pgpSlug: "${slug}"`
    filters.push(slugQuery)
    if (includeServerCustomizations) {
      const attributesQuery = activeServerCustomizations
        .filter((el) => el.selected || typeof el.selected === 'number')
        .map((serverCustomization) => {
          if (serverCustomization.selected?.toString()?.toLowerCase() === allOptionsValue) {
            const allOptionsQuery = serverCustomization.options
              .filter((option) => option?.toString()?.toLowerCase() !== allOptionsValue)
              .map((option) => `${serverCustomization.serverField}: "${option}"`)
              .join(' OR ')

            return `(${allOptionsQuery})`
          } else {
            return `${serverCustomization.serverField}: "${serverCustomization.selected}"`
          }
        })
        .join(' AND ')
      filters.push(attributesQuery)
    }
    return filters.filter((el) => el).join(' AND ')
  }

  function getSalesToStock({
    unit = '',
    qtyTable = {},
    isCellProduct = false,
    isVialsPerDonorProduct = false,
  } = {}) {
    if (isVialsPerDonorProduct) return qtyTable.vialsPerDonor
    if (unit === 'each' || unit === 'vial') return 1
    if (isCellProduct) return qtyTable.aliquots
    return qtyTable.qtyPerLot || 1
  }

  function isCustomAliquot(customizations = []) {
    const isToggleAliquotExists = customizations.some((el) => el.tableField === 'toggleAliquot')
    const isAliquotQtyVisible = customizations.some((el) => el.tableField === 'aliquotQty')
    return isToggleAliquotExists && isAliquotQtyVisible
  }

  function stripNonNumerics(input) {
    return input?.toString().replace(/[^0-9.]/g, '')
  }

  function getActiveTableFieldSelectedValue(tableField, visibleCustomizations) {
    const allSelectedVolPerLot = visibleCustomizations.filter((el) => el.tableField === tableField)
    return allSelectedVolPerLot[allSelectedVolPerLot.length - 1]?.selected
  }

  function isComparisonValid(tableField, value, visibleCustomizations, qtyTable) {
    const isCurrentVolPerLotNumerical = stripNonNumerics(
      getActiveTableFieldSelectedValue('volPerLot', visibleCustomizations),
    )
    if (!isCurrentVolPerLotNumerical) return true

    const lotList = ['volPerLot']
    const aliquotList = ['aliquotQty']
    const isLotListTableField = lotList.includes(tableField)
    const isAliquotTableField = aliquotList.includes(tableField)
    if (isLotListTableField) return value >= qtyTable.aliquotQty
    if (isAliquotTableField) return value <= qtyTable.qtyPerLot
    return true
  }

  function isNumericValid(qty, isCellProduct) {
    const isValidNum = (qty || typeof qty === 'number') && /^\d*\.?\d{0,2}$/.test(qty)
    const topLimit = isCellProduct ? 9999.99 : 99999.99
    const topLimitExceeded = +qty > topLimit
    return isValidNum && !topLimitExceeded
  }

  function isForceCustom(visibleCustomizations) {
    return visibleCustomizations.some((el) => el.selected && el.forceCustom)
  }

  function validateCustomizationValue(
    customization,
    isCellProduct,
    visibleCustomizations,
    qtyTable,
  ) {
    const tableField = customization.tableField
    const value = customization.selected
    const isFreeText = !!customization.textarea

    let failedComparisonTest = []
    let failedNumericTest = []

    const handleComparisonValidation = (tableField, value) => {
      const isValid = isComparisonValid(tableField, value, visibleCustomizations, qtyTable)
      if (isValid) {
        failedComparisonTest = []
      } else {
        failedComparisonTest.push(tableField)
      }
    }

    const freeTextTableFields = ['volPerLot', 'aliquotQty', 'customCellsPerLot', 'customCellQty']
    const dropdownTableFields = ['cellQty']
    const isFreeTextTableField = freeTextTableFields.includes(tableField)
    const isDropdownTableField = dropdownTableFields.includes(tableField)

    if (isFreeTextTableField) {
      const selectedTableField = getActiveTableFieldSelectedValue(tableField, visibleCustomizations)
      const cleanedSelectedTableField = isFreeText
        ? selectedTableField
        : stripNonNumerics(selectedTableField)
      const isValid = isNumericValid(cleanedSelectedTableField, isCellProduct)
      if (isValid || !cleanedSelectedTableField) {
        failedNumericTest = failedNumericTest.filter((el) => el !== tableField)
        handleComparisonValidation(tableField, cleanedSelectedTableField)
      } else {
        failedNumericTest.push(tableField)
      }
    } else if (isDropdownTableField) {
      const coeff = +value?.toString()?.toLowerCase().split('x')[0]
      if (!isNaN(coeff)) handleComparisonValidation(tableField, coeff)
    }
    return { failedComparisonTest, failedNumericTest }
  }

  function validateConfigurationsChange(
    product,
    customizations,
    configurationsRelationshipEntries,
    resetTableValue,
  ) {
    const loopLimit = 10
    let loopCount = 0
    const action = () => {
      ++loopCount
      const prevConfigurationSeletions = customizations.reduce((sum, curr) => {
        sum[curr.title] = curr.selected
        return sum
      }, {})
      if (!configurationsRelationshipEntries?.length) return
      const activeConfigFields = configurationsRelationshipEntries
        .map((configFieldEntry) => {
          const customizationField = customizations.find(
            (el) => el.title === configFieldEntry.configurationName,
          )
          if (!customizationField) return null
          const notSelectedOptionName = '$NOT_SELECTED'
          const activeConfigFieldOptions = configFieldEntry.configurationOptions
            .map((configFieldOptionEntry) => {
              if (
                !configFieldOptionEntry.fields ||
                configFieldOptionEntry.sys?.contentType?.sys?.id !== 'productOptionsRelationship'
              )
                return null
              const isOptionSelected = configFieldOptionEntry.fields.fieldValues.includes(
                customizationField.selected || notSelectedOptionName,
              )
              const existingAffectedFields =
                configFieldOptionEntry.fields.affectedFields?.filter((affectedFieldEntry) => {
                  return customizations.some(
                    (el) => el.title === affectedFieldEntry.fields?.fieldName,
                  )
                }) || []
              if (!isOptionSelected || !existingAffectedFields.length) return null
              return existingAffectedFields.map((affectedField) => ({
                fieldName: configFieldOptionEntry.fields.fieldName,
                fieldValues: configFieldOptionEntry.fields.fieldValues,
                affectedFieldName: affectedField.fields.fieldName,
                whitelistedValues: affectedField.fields.fieldValues,
              }))
            })
            .filter((el) => el)
          return activeConfigFieldOptions.length ? activeConfigFieldOptions.flat() : null
        })
        .filter((el) => el)
      const activeConfigItems = activeConfigFields.flat()
      const whitelistMap = activeConfigItems.reduce((sum, curr) => {
        sum[curr.affectedFieldName] = sum[curr.affectedFieldName]
          ? sum[curr.affectedFieldName].filter((el) => curr.whitelistedValues.includes(el))
          : curr.whitelistedValues
        return sum
      }, {})

      customizations.forEach((customization, customizationIdx) => {
        const isListCustomization =
          customization.allIconGroup?.length || customization.allOptions?.length

        const whitelist = whitelistMap[customization.title] || []
        const isWhitelistRuleRequired = !!whitelist?.length
        const isHideRule = isWhitelistRuleRequired && whitelist.some((el) => el === '$HIDDEN')
        if (isListCustomization) {
          const isIconsField = customization.allIconGroup?.length
          const optionPropertyName = isIconsField ? 'iconGroup' : 'options'
          const allOptionsPropertyName = isIconsField ? 'allIconGroup' : 'allOptions'
          const whitelistedOptionsFromAllOptionsList =
            customization[allOptionsPropertyName]?.filter((el) =>
              whitelist?.includes(isIconsField ? el.title : el),
            ) || []
          const isAffectedFieldContainsWhitelistedValues =
            whitelistedOptionsFromAllOptionsList.length

          customization[optionPropertyName] =
            isWhitelistRuleRequired && isAffectedFieldContainsWhitelistedValues
              ? customization[allOptionsPropertyName].filter((option) =>
                  whitelist.includes(isIconsField ? option.title : option),
                )
              : [...customization[allOptionsPropertyName]]
          const updateFieldSelection = () => {
            const whitelistedOptionsAmount = customization[optionPropertyName]?.length
            if (!whitelistedOptionsAmount) {
              customization.selected = null
            } else if (whitelistedOptionsAmount === 1) {
              const firstOption = customization[optionPropertyName][0]
              customization.selected = isIconsField ? firstOption.title : firstOption
            } else {
              if (
                isWhitelistRuleRequired &&
                isAffectedFieldContainsWhitelistedValues &&
                !whitelist.includes(customization.selected)
              ) {
                customization.selected = null
              }
            }
          }
          updateFieldSelection()
        }

        if (isHideRule && !customization.isHidden) {
          const parentCustomization =
            customization.parent &&
            customizations.find((el) => el.title === customization.parent.title)
          const isParentSameTableField =
            parentCustomization && parentCustomization.tableField === customization.tableField
          customization.isHidden = true
          customization.selected = customization.defaultValue
          if (customization.tableField && (!parentCustomization || !isParentSameTableField)) {
            resetTableValue(customization.tableField, product)
          }
        } else if (!isHideRule && customization.isHidden) {
          customization.isHidden = false
        }
        const prevCustomization = customizations[customizationIdx - 1]
        if (prevCustomization) displayHiddenFreeText(prevCustomization.title, customizations)
      })

      const isSomeSelectionChanged = customizations.some(
        (customization) =>
          customization.selected !== prevConfigurationSeletions[customization.title],
      )
      if (isSomeSelectionChanged && loopCount < loopLimit) action()
    }
    action()
  }

  function checkParentSelection(
    childCustomization,
    customizations,
    configurationsRelationshipEntries,
  ) {
    if (!childCustomization.parent || !configurationsRelationshipEntries?.length) return
    let configData
    configurationsRelationshipEntries.forEach((entry) => {
      if (entry.configurationName !== childCustomization.parent.title) return false
      entry.configurationOptions?.forEach((configurationOption) => {
        const affectedItem = configurationOption.fields?.affectedFields?.find(
          (affectedField) =>
            affectedField.fields?.fieldName === childCustomization.title &&
            affectedField.fields?.fieldValues?.some(
              (affectedFieldValue) => affectedFieldValue === '$HIDDEN',
            ),
        )
        if (affectedItem) {
          configData = {
            parentTitle: configurationOption.fields?.fieldName,
            parentValues: configurationOption.fields?.fieldValues,
          }
        }
      })
    })
    if (configData) {
      const parentCustomization = customizations.find((el) => el.title === configData.parentTitle)
      const optionToSelect = parentCustomization?.options?.find(
        (option) => !configData.parentValues.includes(option),
      )
      if (optionToSelect) parentCustomization.selected = optionToSelect
    }
  }

  function displayHiddenFreeText(title, customizations = []) {
    customizations.forEach((el, i, arr) => {
      const selected = `${el.selected}`.toLowerCase()

      if (
        (selected.includes('other') || selected.includes('custom')) &&
        el.title === title &&
        _.get(arr[i + 1], 'textarea')
      ) {
        arr[i + 1].textarea.uiDisplay = true
      } else if (
        el.title === title &&
        _.get(arr[i + 1], 'textarea.uiDisplay') &&
        !_.get(arr[i + 1], 'textarea.initialUiDisplay')
      ) {
        arr[i + 1].textarea.uiDisplay = false
        arr[i + 1].selected = arr[i + 1].defaultValue
      }
    })
  }

  return {
    transformProduct,
    extactCustomizations,
    updateTableValue,
    updateQtyTableBySelectedCustomizations,
    isSomeChildCustomizationVisible,
    getCustomizationsAddToCartData,
    getAddToCartProductData,
    getPriceParams,
    getVisibleCustomizations,
    getActiveServerCustomizations,
    getSkuFiltersString,
    getSalesToStock,
    isCustomAliquot,
    stripNonNumerics,
    getActiveTableFieldSelectedValue,
    isComparisonValid,
    isNumericValid,
    isForceCustom,
    validateCustomizationValue,
    validateConfigurationsChange,
    checkParentSelection,
    displayHiddenFreeText,
  }
}
